import { Button, FormGroup, Input, Label } from 'reactstrap';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { TimeRangeSelector } from '@g17eco/molecules/time-range-selector';
import { FilterSurveyPacks } from '../partials/FilterSurveyPacks';
import { downloadBenchmarkingSurveys } from './utils';
import { useIndustrySector } from '@features/industry-sector-selector/useIndustrySector';
import { RequestScope } from '@actions/api';
import { SURVEY } from '@constants/terminology';
import { ExchangeSurveyWithCompany } from './types';
import { type SurveyFilters } from '@g17eco/types/portfolio-benchmarking';

const dateFieldOptions = [
  { label: `${SURVEY.CAPITALIZED_ADJECTIVE} date`, value: 'effectiveDate' },
  { label: 'Completed date', value: 'completedDate' },
] as const;

interface LatestSurveyFiltersProps {
  filteredSurveys: ExchangeSurveyWithCompany[];
  filters: SurveyFilters;
  onChangeFilters: (filters: SurveyFilters) => void;
}

export const LatestSurveyFilters = (props: LatestSurveyFiltersProps) => {
  const { filteredSurveys, filters, onChangeFilters } = props;
  const { sectorOptions, industryOptions } = useIndustrySector(filters.sector);

  const handleChangeFilters = (name: string, option: Option | RequestScope[]) => {
    const newFilters = {
      ...filters,
      [name]: Array.isArray(option) ? option.map((option) => option.code) : option.value,
    };
    onChangeFilters(newFilters);
  };

  const handleChangeSectorIndustryFilters = (name: string, option: Option | null) => {
    let newFilters = { ...filters };
    if (name === 'sector') {
      // reset industry if sector change
      newFilters = { ...newFilters, industry: null };
    }
    newFilters = { ...newFilters, [name]: option?.value ? option?.label : null };

    onChangeFilters(newFilters);
  };

  return (
    <div className='latest-survey-filters'>
      <div className='d-flex justify-content-between align-items-center gap-3 filters-wrapper'>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder='Sector'
          isClearable
          className='flex-grow-1'
          options={sectorOptions}
          value={sectorOptions.find((item) => item.label === filters.sector) ?? null}
          onChange={(option) => handleChangeSectorIndustryFilters('sector', option)}
        />
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder='Industry'
          isClearable
          className='flex-grow-1'
          options={industryOptions}
          value={industryOptions.find((item) => item.label === filters.industry) ?? null}
          onChange={(option) => handleChangeSectorIndustryFilters('industry', option)}
          isDisabled={!filters.sector}
        />
        <div className='flex-grow-1'>
          <SimpleTooltip text='Filter currently unavailable'>
            <SelectFactory
              selectType={SelectTypes.SingleSelect}
              placeholder='Market cap'
              options={[]}
              onChange={(option) => (option ? handleChangeFilters('marketCap', option) : undefined)}
              isDisabled
            />
          </SimpleTooltip>
        </div>
        <div className='flex-grow-1'>
          <FilterSurveyPacks
            selectedScopes={filters.surveyPacks}
            handleSurveyPackFilter={(option: RequestScope[]) => handleChangeFilters('surveyPacks', option)}
          />
        </div>
      </div>
      <div className='d-flex justify-content-between align-items-center gap-2 mt-5'>
        <div className='d-flex align-items-center gap-2'>
          <span>Date range:</span>
          <TimeRangeSelector
            timeRange={filters.timeRange}
            dateRange={filters.dateRange}
            onChangeDateRange={(dateRange, period) => onChangeFilters({ ...filters, dateRange, timeRange: period })}
            styleProps={{ minWidth: 150 }}
          />
          <SelectFactory
            selectType={SelectTypes.SingleSelect}
            placeholder='Date field'
            options={dateFieldOptions}
            onChange={(option) => (option ? handleChangeFilters('dateField', option) : undefined)}
            isSearchable={false}
            minWidth={150}
            value={dateFieldOptions.find((item) => item.value === filters.dateField)}
          />
        </div>
        <div className='d-flex align-items-center gap-2'>
          <span>Show:</span>
          <FormGroup check key='latestSurveysOnly'>
            <Input
              type='checkbox'
              id='latestSurveysOnly'
              name='latestSurveysOnly'
              onChange={(e) => onChangeFilters({ ...filters, latestSurveysOnly: e.currentTarget.checked })}
              checked={filters.latestSurveysOnly}
            />
            <Label check for='latestSurveysOnly'>
              Latest {SURVEY.PLURAL} only
            </Label>
          </FormGroup>
          <FormGroup check key='completedSurveysOnly'>
            <Input
              type='checkbox'
              id='completedSurveysOnly'
              name='completedSurveysOnly'
              onChange={(e) => onChangeFilters({ ...filters, completedSurveysOnly: e.currentTarget.checked })}
              checked={filters.completedSurveysOnly}
            />
            <Label check for='completedSurveysOnly'>
              Completed {SURVEY.PLURAL} only
            </Label>
          </FormGroup>
        </div>
        <Button
          onClick={() => downloadBenchmarkingSurveys(filteredSurveys)}
          color='secondary'
          className='d-flex align-items-center'
        >
          <i className='fal fa-file-excel mr-2' />
          Download
        </Button>
      </div>
    </div>
  );
};
